import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMapPin, FiSearch, FiPlus, FiCheck, FiX, FiGlobe, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { searchCombinedGolfCoursesProgressive } from '../services/customGolfCourseService';
import { useAuth } from '../contexts/AuthContext';
import { isGolfCourseKnown } from '../services/geocodingService';
import { supabase } from '../lib/supabase';
import { formatClubDisplayName } from '../utils/clubDisplay';
import { resolveTimezoneFromCoords } from '../utils/timeHelpers';

// Cycling progress messages shown while the AI lookup is in flight.
// Mirrors the smart-schedule pattern (SmartScheduleSetup.jsx:160) - 1.2s
// per message, parks on the last one.
const LOOKUP_MESSAGES = [
  { text: 'Looking up the course…', icon: FiSearch },
  { text: 'Finding the address…', icon: FiMapPin },
  { text: 'Standardising course details…', icon: FiCheck },
];

function GolfClubAutocomplete({
  value,
  selectedCourse,
  onChange,
  onCourseSelect,
  placeholder = "Type first word of course name...",
  error,
  disabled = false,
  className = ""
}) {
  // Handle both prop patterns
  const courseValue = formatClubDisplayName(selectedCourse) || value || '';
  const handleChange = onCourseSelect || onChange;
  
  const [inputValue, setInputValue] = useState(courseValue);
  const [suggestions, setSuggestions] = useState([]);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchComplete, setSearchComplete] = useState(false);
  // AI lookup state
  const [aiLookupState, setAiLookupState] = useState('idle'); // idle | input | searching | confirming | picker | result | error
  const [aiQuery, setAiQuery] = useState('');
  const [aiClub, setAiClub] = useState(null);       // club-level metadata from lookup
  const [aiCourses, setAiCourses] = useState([]);   // array of courses (1 or many)
  const [aiResult, setAiResult] = useState(null);   // chosen club+course (after picker, or auto for single-course)
  const [aiError, setAiError] = useState(null);
  const [aiLastRequestTime, setAiLastRequestTime] = useState(0);
  const [aiLoadingStage, setAiLoadingStage] = useState(0); // index into LOOKUP_MESSAGES while searching
  const { user } = useAuth();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const searchIdRef = useRef(0); // Track search requests to handle race conditions

  // Check if weather data is available for a golf course
  const hasWeatherSupport = (suggestion) => {
    // Check if the suggestion has coordinates directly from the database
    if (suggestion.has_coordinates || (suggestion.latitude && suggestion.longitude)) {
      return true;
    }
    
    // Fallback to the geocoding service check for backwards compatibility
    return isGolfCourseKnown(suggestion.name);
  };

  // Validate and format golf course name to proper case
  const formatGolfCourseName = (name) => {
    if (!name || typeof name !== 'string') return '';
    
    // Trim whitespace
    const trimmed = name.trim();
    if (!trimmed) return '';
    
    // Words that should remain lowercase (articles, prepositions, etc.)
    const lowercaseWords = ['and', 'at', 'by', 'for', 'in', 'of', 'on', 'the', 'to', 'with'];
    
    // Split into words and format each one
    const words = trimmed.toLowerCase().split(/\s+/);
    
    const formattedWords = words.map((word, index) => {
      // Always capitalize the first word
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      
      // Keep certain words lowercase unless they're the first word
      if (lowercaseWords.includes(word)) {
        return word;
      }
      
      // Capitalize other words
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
    
    return formattedWords.join(' ');
  };

  // Validate golf course name
  const validateGolfCourseName = (name) => {
    const formatted = formatGolfCourseName(name);
    
    // Check minimum length
    if (formatted.length < 3) {
      return { isValid: false, error: 'Course name must be at least 3 characters long', formatted };
    }
    
    // Check maximum length
    if (formatted.length > 100) {
      return { isValid: false, error: 'Course name must be less than 100 characters', formatted };
    }
    
    // Check for invalid characters (allow letters, numbers, spaces, hyphens, apostrophes, periods)
    const validPattern = /^[a-zA-Z0-9\s\-'.&]+$/;
    if (!validPattern.test(formatted)) {
      return { isValid: false, error: 'Course name contains invalid characters', formatted };
    }
    
    // Check that it's not just numbers or special characters
    const hasLetters = /[a-zA-Z]/.test(formatted);
    if (!hasLetters) {
      return { isValid: false, error: 'Course name must contain letters', formatted };
    }
    
    return { isValid: true, error: null, formatted };
  };

  // Check if we should search - either space detected OR 3+ characters
  const shouldSearch = (text) => {
    const trimmed = text.trim();
    return trimmed.length >= 3 || text.includes(' ');
  };

  // Search for golf clubs (both public and custom) with progressive loading
  const searchGolfClubs = async (searchTerm) => {
    const trimmed = searchTerm.trim();

    // Only search after 3+ characters OR a space (whichever comes first)
    if (!shouldSearch(searchTerm)) {
      setSuggestions([]);
      setSearchComplete(false);
      return;
    }

    // Increment search ID to track this specific search
    const currentSearchId = ++searchIdRef.current;

    setIsLoading(true);
    setSearchComplete(false);

    try {
      await searchCombinedGolfCoursesProgressive(searchTerm, ({ results, isStandardising }) => {
        // Ignore results from stale searches
        if (currentSearchId !== searchIdRef.current) return;

        // Update suggestions with current results (progressively revealed)
        setSuggestions(results);
        setIsLoading(false);
        setSearchComplete(!isStandardising); // Only complete when not standardising
      });
    } catch (error) {
      console.error('Error searching golf clubs:', error);
      if (currentSearchId === searchIdRef.current) {
        setSuggestions([]);
        setIsLoading(false);
        setSearchComplete(true); // Search failed but is complete
      }
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);
    setSelectedIndex(-1);
    setSearchComplete(false); // Reset immediately when user types

    // Exit AI lookup when user starts typing
    if (aiLookupState !== 'idle') {
      resetAiLookup();
    }

    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout for search
    debounceRef.current = setTimeout(() => {
      searchGolfClubs(value);
    }, 300);

    // If the user is manually editing and the value doesn't match the selected course,
    // clear the selected course to enable proper autocomplete behavior
    if (selectedCourse && value !== formatClubDisplayName(selectedCourse) && handleChange && onCourseSelect) {
      handleChange(null); // Clear the selected course object
    }
    
    // Also call the onChange handler for components that expect string values
    if (handleChange && !onCourseSelect) {
      handleChange(value);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    setInputValue(formatClubDisplayName(suggestion));
    if (handleChange) {
      // If using onCourseSelect pattern, pass the full suggestion object
      if (onCourseSelect) {
        handleChange(suggestion);
      } else {
        // If using onChange pattern, pass just the name
        handleChange(suggestion.name);
      }
    }
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle clearing the selected course
  const handleClearCourse = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setInputValue('');
    if (handleChange) {
      if (onCourseSelect) {
        handleChange(null);
      } else {
        handleChange('');
      }
    }
    setShowSuggestions(false);
    setSuggestions([]);
    // Focus the input after clearing and remove readOnly
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
  };

  // Open the AI lookup panel
  const handleOpenAiLookup = () => {
    setAiQuery(inputValue || '');
    setAiClub(null);
    setAiCourses([]);
    setAiResult(null);
    setAiError(null);
    setAiLookupState('input');
    setShowSuggestions(false);
  };

  // Reset AI lookup state
  const resetAiLookup = () => {
    setAiLookupState('idle');
    setAiQuery('');
    setAiClub(null);
    setAiCourses([]);
    setAiResult(null);
    setAiError(null);
  };

  // Merge a chosen course onto the club to produce a single upsert payload.
  const mergeClubAndCourse = (club, course) => ({
    ...club,
    course_name: course?.course_name ?? null,
    number_of_holes: course?.number_of_holes ?? null,
    booking_url: course?.booking_url ?? null,
    golfnow_facility_id: course?.golfnow_facility_id ?? null,
  });

  // Call the AI lookup edge function
  const handleAiLookup = async () => {
    if (!aiQuery.trim() || aiQuery.trim().length < 3) return;

    // Throttle: minimum 5 seconds between requests
    const now = Date.now();
    if (now - aiLastRequestTime < 5000) return;
    setAiLastRequestTime(now);

    setAiLookupState('searching');
    setAiError(null);

    try {
      const { data, error } = await supabase.functions.invoke('lookup-golf-course', {
        body: { query: aiQuery.trim() }
      });

      if (error) {
        setAiError('Something went wrong. Please try again.');
        setAiLookupState('error');
        return;
      }

      if (!data?.success) {
        setAiError(data?.error || 'Something went wrong. Please try again.');
        setAiLookupState('error');
        return;
      }

      if (!data.club || !Array.isArray(data.courses) || data.courses.length === 0) {
        setAiError(data.message || "We couldn't find that course. Try adding more details like the town or country.");
        setAiLookupState('error');
        return;
      }

      setAiClub(data.club);
      setAiCourses(data.courses);

      if (data.courses.length === 1) {
        // Single-course club - skip the picker.
        setAiResult(mergeClubAndCourse(data.club, data.courses[0]));
        setAiLookupState('result');
      } else {
        // Multi-course club - ask the user which course.
        setAiLookupState('picker');
      }
    } catch (e) {
      console.error('AI lookup error:', e);
      setAiError('Something went wrong. Please try again.');
      setAiLookupState('error');
    }
  };

  // User picked a course from the multi-course picker.
  const handlePickCourse = (course) => {
    if (!aiClub) return;
    setAiResult(mergeClubAndCourse(aiClub, course));
    setAiLookupState('result');
  };

  // Columns we fetch back from golf_clubs across the various dedup / insert paths.
  const SELECT_COLS = 'id, name, course_name, latitude, longitude, timezone, address, city, state, country, country_code, google_place_id, golfnow_facility_id, golfnow_course_name, booking_url';

  // Confirm the AI-found course: check for duplicates, insert into golf_clubs, and select
  const handleConfirmAiCourse = async () => {
    if (!aiResult || !user) return;

    setAiLookupState('confirming'); // Brief "Saving course…" while we upsert

    try {
      // Dedup by (name, course_name) - case-insensitive on name, exact-null-safe on course_name.
      let existingQuery = supabase
        .from('golf_clubs')
        .select(SELECT_COLS)
        .ilike('name', aiResult.name);
      existingQuery = aiResult.course_name
        ? existingQuery.ilike('course_name', aiResult.course_name)
        : existingQuery.is('course_name', null);
      const { data: existingByName } = await existingQuery.limit(1).maybeSingle();

      if (existingByName) {
        // Course already exists - use it directly
        const courseObj = {
          ...existingByName,
          source: 'database',
          has_coordinates: !!(existingByName.latitude && existingByName.longitude)
        };
        handleSelectSuggestion(courseObj);
        resetAiLookup();
        return;
      }

      // Coordinate-proximity dedup - only fires for single-course rows so we don't
      // collapse legitimate multi-course siblings (Brabazon and PGA share coords).
      if (!aiResult.course_name && aiResult.latitude && aiResult.longitude) {
        const { data: nearbyMatches } = await supabase
          .from('golf_clubs')
          .select(SELECT_COLS)
          .is('course_name', null)
          .gte('latitude', aiResult.latitude - 0.01)
          .lte('latitude', aiResult.latitude + 0.01)
          .gte('longitude', aiResult.longitude - 0.01)
          .lte('longitude', aiResult.longitude + 0.01)
          .limit(5);

        if (nearbyMatches && nearbyMatches.length > 0) {
          const nameLower = aiResult.name.toLowerCase();
          const closeMatch = nearbyMatches.find(m =>
            m.name.toLowerCase().includes(nameLower.split(' ')[0]) ||
            nameLower.includes(m.name.toLowerCase().split(' ')[0])
          );
          if (closeMatch) {
            const courseObj = {
              ...closeMatch,
              source: 'database',
              has_coordinates: !!(closeMatch.latitude && closeMatch.longitude)
            };
            handleSelectSuggestion(courseObj);
            resetAiLookup();
            return;
          }
        }
      }

      // Resolve the IANA timezone from the Linkup lat/lng before insert so
      // every new club row has a populated timezone column from day one,
      // matching the backfill behaviour. Read-time fallbacks still work for
      // legacy rows, but this means we don't accumulate new tz-NULL rows.
      const resolvedTimezone = resolveTimezoneFromCoords(
        aiResult.latitude,
        aiResult.longitude,
      );

      // Insert new course into golf_clubs (no onConflict - legacy unique on name was dropped).
      const { data: newCourse, error: insertError } = await supabase
        .from('golf_clubs')
        .insert({
          name: aiResult.name,
          course_name: aiResult.course_name ?? null,
          address: aiResult.address,
          city: aiResult.city,
          state: aiResult.state,
          country: aiResult.country,
          country_code: aiResult.country_code,
          postcode: aiResult.postcode,
          latitude: aiResult.latitude,
          longitude: aiResult.longitude,
          timezone: resolvedTimezone,
          website: aiResult.website,
          booking_url: aiResult.booking_url ?? null,
          phone: aiResult.phone,
          golfnow_facility_id: aiResult.golfnow_facility_id ?? null,
          source: 'manual'
        })
        .select(SELECT_COLS)
        .single();

      if (insertError) {
        console.error('Error inserting course:', insertError);
        setAiError('Failed to add course. Please try again.');
        setAiLookupState('error');
        return;
      }

      const courseObj = {
        ...newCourse,
        source: 'database',
        has_coordinates: !!(newCourse.latitude && newCourse.longitude)
      };
      handleSelectSuggestion(courseObj);
      resetAiLookup();
    } catch (e) {
      console.error('Error confirming AI course:', e);
      setAiError('Failed to add course. Please try again.');
      setAiLookupState('error');
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault(); // Always prevent Enter from submitting form
        if (showSuggestions && suggestions.length > 0 && selectedIndex >= 0) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        if (showSuggestions) {
          setShowSuggestions(false);
        }
        break;
    }
  };

  // Cycle through LOOKUP_MESSAGES while the AI lookup is in flight.
  useEffect(() => {
    if (aiLookupState !== 'searching') {
      setAiLoadingStage(0);
      return;
    }
    const id = setInterval(() => {
      setAiLoadingStage((s) =>
        s + 1 >= LOOKUP_MESSAGES.length ? LOOKUP_MESSAGES.length - 1 : s + 1
      );
    }, 1200);
    return () => clearInterval(id);
  }, [aiLookupState]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        if (aiLookupState !== 'idle') resetAiLookup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input value when prop changes (only if not currently showing suggestions)
  useEffect(() => {
    // Only update if we're not actively showing suggestions (user is not typing)
    if (!showSuggestions) {
      setInputValue(formatClubDisplayName(selectedCourse) || value || '');
    }
  }, [value, selectedCourse, showSuggestions]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        {/* Input field - consistent styling for both selected and empty states */}
        <input
          ref={inputRef}
          type="text"
          value={selectedCourse ? formatClubDisplayName(selectedCourse) : inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (selectedCourse) {
              // If a course is selected and user focuses, clear it to allow editing
              handleClearCourse({ preventDefault: () => {}, stopPropagation: () => {} });
            } else if (inputValue.length >= 3) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 font-medium min-h-[52px] text-text-primary placeholder-text-muted ${
            error ? 'border-status-error' : 'border-border-subtle'
          } ${disabled ? 'bg-surface cursor-not-allowed' : 'bg-surface focus:bg-card'}`}
        />
        
        {/* Clear button - appears when course is selected */}
        {selectedCourse && !disabled && (
          <button
            type="button"
            onClick={handleClearCourse}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full bg-text-muted hover:bg-text-secondary flex items-center justify-center transition-colors duration-200 opacity-60 hover:opacity-80"
            title="Clear course"
          >
            <SafeIcon icon={FiX} className="w-2.5 h-2.5 text-white" />
          </button>
        )}
        
        {/* Loading spinner - only show when not selected and loading */}
        {isLoading && !selectedCourse && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-status-error">{error}</p>
      )}

      {/* Helper text - shown when typing but not enough characters yet */}
      {!error && !selectedCourse && inputValue.length > 0 && !shouldSearch(inputValue) && !isLoading && (
        <p className="mt-1 text-xs text-text-muted">Keep typing to search...</p>
      )}

      {/* Suggestions dropdown */}
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-card rounded-xl shadow-card border border-border-subtle overflow-hidden"
          >
            <div className="max-h-64 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <motion.button
                  key={suggestion.id || suggestion.external_id || `skeleton-${index}`}
                  type="button"
                  whileHover={{ backgroundColor: suggestion.isLoading ? undefined : 'var(--bg-subtle)' }}
                  onClick={() => !suggestion.isLoading && handleSelectSuggestion(suggestion)}
                  disabled={suggestion.isLoading}
                  className={`w-full px-4 py-3 text-left transition-colors duration-200 flex items-start space-x-3 ${
                    suggestion.isLoading ? 'cursor-default' : 'hover:bg-surface'
                  } ${index === selectedIndex && !suggestion.isLoading ? 'bg-surface' : ''
                  } ${index !== suggestions.length - 1 ? 'border-b border-border-subtle' : ''}`}
                >
                  <div className="w-full">
                    <div className="flex items-center space-x-3">
                      {/* Icon - show skeleton or real icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        suggestion.isLoading
                          ? 'bg-subtle animate-pulse'
                          : suggestion.is_custom ? 'bg-accent-yellow/10' : 'bg-accent/10'
                      }`}>
                        {!suggestion.isLoading && (
                          <SafeIcon icon={FiMapPin} className={`w-5 h-5 ${
                            suggestion.is_custom ? 'text-accent-yellow' : 'text-accent'
                          }`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Name - show skeleton or real name */}
                        {suggestion.isLoading ? (
                          <>
                            <div className="h-5 bg-subtle rounded-lg animate-pulse w-3/4 mb-1.5"></div>
                            <div className="h-4 bg-surface rounded-lg animate-pulse w-1/2"></div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center space-x-2">
                              <div className="font-bold text-text-primary truncate">
                                {formatClubDisplayName(suggestion)}
                              </div>
                              {suggestion.is_custom && (
                                <span className="text-xs bg-accent-yellow/10 text-accent-yellow px-2 py-1 rounded-full font-medium flex-shrink-0">
                                  Custom
                                </span>
                              )}
                            </div>
                            {/* Location details */}
                            {(suggestion.city || suggestion.state) && (
                              <div className="text-sm text-text-muted truncate mt-0.5">
                                {[suggestion.city, suggestion.state].filter(Boolean).join(', ')}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {/* Loading spinner for items being standardised */}
                      {suggestion.isLoading && (
                        <div className="flex-shrink-0">
                          <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Add new course option - sticky at bottom, outside scrollable area */}
            {user && (
              <button
                type="button"
                onClick={handleOpenAiLookup}
                className="w-full px-4 py-3 text-left hover:bg-accent/5 transition-colors duration-200 flex items-center space-x-3 border-t border-border-subtle"
              >
                <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiPlus} className="w-4 h-4 text-accent" />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium text-accent">Can't find your course? Add it here</span>
                </div>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* No results - show Add new course option */}
      <AnimatePresence>
        {showSuggestions && shouldSearch(inputValue) && suggestions.length === 0 && searchComplete && !selectedCourse && user && aiLookupState === 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-card rounded-xl shadow-card border border-border-subtle overflow-hidden"
          >
            <div className="px-4 py-3 text-center text-sm text-text-muted border-b border-border-subtle">
              No courses found
            </div>
            <motion.button
              type="button"
              whileHover={{ backgroundColor: 'rgba(var(--color-accent-rgb, 34 197 94) / 0.08)' }}
              onClick={handleOpenAiLookup}
              className="w-full px-4 py-3 text-left hover:bg-accent/5 transition-colors duration-200 flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiPlus} className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-text-primary">
                  Add new course
                </div>
                <div className="text-sm text-text-muted">
                  Can't find your course? Let us look it up
                </div>
              </div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Lookup Panel */}
      <AnimatePresence>
        {aiLookupState !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-card rounded-xl border border-border-subtle overflow-hidden shadow-card"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-accent/5 border-b border-border-subtle flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiGlobe} className="w-4 h-4 text-accent" />
                <span className="font-medium text-text-primary text-sm">Add new course</span>
              </div>
              <button
                type="button"
                onClick={resetAiLookup}
                className="text-text-muted hover:text-text-secondary transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>

            {/* Input state */}
            {aiLookupState === 'input' && (
              <div className="px-4 py-4">
                <p className="text-sm text-text-muted mb-3">
                  Describe your course - include the name, town/city, country, or any details that help us find it.
                </p>
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAiLookup();
                    }
                  }}
                  placeholder="e.g. Pleasington Golf Club, Blackburn, Lancashire"
                  autoFocus
                  className="w-full px-3 py-2.5 border border-border-subtle rounded-lg text-sm focus:ring-2 focus:ring-accent focus:border-transparent bg-surface text-text-primary placeholder-text-muted"
                />
                <button
                  type="button"
                  onClick={handleAiLookup}
                  disabled={!aiQuery.trim() || aiQuery.trim().length < 3}
                  className="mt-3 w-full py-2.5 bg-accent text-white rounded-md font-medium text-sm hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiSearch} className="w-4 h-4" />
                  Search for course
                </button>
              </div>
            )}

            {/* Searching state - cycling progress messages */}
            {aiLookupState === 'searching' && (
              <div className="px-4 py-6">
                <div className="flex flex-col items-center text-center">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={aiLoadingStage}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      className="flex items-center gap-2 mb-2"
                      aria-live="polite"
                    >
                      <SafeIcon
                        icon={LOOKUP_MESSAGES[aiLoadingStage].icon}
                        className="w-5 h-5 text-accent animate-pulse"
                      />
                      <p className="text-sm font-medium text-text-primary">
                        {LOOKUP_MESSAGES[aiLoadingStage].text}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                  <p className="text-xs text-text-muted">This usually takes 5-10 seconds</p>
                </div>
              </div>
            )}

            {/* Confirming state - brief save while we upsert to golf_clubs */}
            {aiLookupState === 'confirming' && (
              <div className="px-4 py-5">
                <div className="flex items-center justify-center gap-2">
                  <SafeIcon icon={FiLoader} className="w-4 h-4 text-accent animate-spin" />
                  <p className="text-sm font-medium text-text-primary">Saving course…</p>
                </div>
              </div>
            )}

            {/* Picker state - multi-course club */}
            {aiLookupState === 'picker' && aiClub && aiCourses.length > 1 && (
              <div className="px-4 py-4">
                <p className="text-sm font-medium text-text-primary mb-1">
                  {aiClub.name} has {aiCourses.length} courses
                </p>
                <p className="text-xs text-text-muted mb-3">
                  Which course are you playing?
                </p>
                <div className="space-y-2">
                  {aiCourses.map((course, idx) => (
                    <button
                      key={`${course.course_name ?? 'main'}-${idx}`}
                      type="button"
                      onClick={() => handlePickCourse(course)}
                      className="w-full px-3 py-2.5 text-left rounded-lg border border-border-subtle hover:bg-accent/5 hover:border-accent/40 transition-colors flex items-center justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-text-primary truncate">
                          {course.course_name || aiClub.name}
                        </div>
                        <div className="text-xs text-text-muted mt-0.5">
                          {[
                            course.number_of_holes ? `${course.number_of_holes} holes` : null,
                            course.golfnow_facility_id ? 'On GolfNow' : null,
                          ].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-text-muted flex-shrink-0 ml-3" />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setAiLookupState('input')}
                  className="mt-3 w-full py-2 text-xs text-text-muted hover:text-text-secondary transition-colors"
                >
                  Not the right club? Try again with more details
                </button>
              </div>
            )}

            {/* Result state */}
            {aiLookupState === 'result' && aiResult && (
              <div className="px-4 py-4">
                <div className="flex items-start space-x-3 mb-4 p-3 bg-accent/5 rounded-lg border border-accent/20">
                  <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-text-primary">{formatClubDisplayName(aiResult)}</div>
                    <div className="text-sm text-text-muted mt-0.5">
                      {[aiResult.address, aiResult.city, aiResult.postcode, aiResult.country].filter(Boolean).join(', ')}
                    </div>
                    {aiResult.website && (
                      <div className="text-xs text-accent mt-1 truncate">{aiResult.website}</div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleConfirmAiCourse}
                    className="flex-1 py-2.5 bg-accent text-white rounded-md font-medium text-sm hover:bg-accent/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    Confirm
                  </button>
                  <button
                    type="button"
                    onClick={() => setAiLookupState('input')}
                    className="flex-1 py-2.5 border border-border-subtle rounded-md font-medium text-sm text-text-secondary hover:bg-surface transition-colors"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {/* Error state */}
            {aiLookupState === 'error' && (
              <div className="px-4 py-5 text-center">
                <p className="text-sm text-text-secondary mb-3">{aiError}</p>
                <button
                  type="button"
                  onClick={() => setAiLookupState('input')}
                  className="text-sm text-accent font-medium hover:text-accent/80 transition-colors"
                >
                  Try again with more details
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

export default GolfClubAutocomplete;